import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart,
  Download,
  Mail,
  Smartphone,
  Loader2,
  Check,
  ArrowLeft,
  Settings,
  Hand,
  Scroll,
} from 'lucide-react';
import { dreamsApi, assetsApi, ViewerData } from '../lib/api';
import { useSettingsStore, useViewerStore } from '../lib/store';
import { mockViewerData, useMockMode } from '../lib/mockData';

export default function DreamViewerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { navigationMode } = useSettingsStore();
  const { setCurrentDream, isBottomBarVisible, toggleBottomBar, hideBottomBar } = useViewerStore();
  const isMock = useMockMode();

  const [viewerData, setViewerData] = useState<ViewerData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showWallpaperSheet, setShowWallpaperSheet] = useState(false);
  const [showSettingsSheet, setShowSettingsSheet] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const isResetting = useRef(false);
  const pointerStart = useRef({ x: 0, y: 0, t: 0 });

  useEffect(() => {
    if (id) loadViewer(id);
  }, [id]);

  const loadViewer = async (dreamId: string) => {
    setIsLoading(true);
    setError('');
    if (isMock) {
      await new Promise((r) => setTimeout(r, 300));
      const data = mockViewerData[dreamId];
      if (data) { setViewerData(data); setCurrentDream(dreamId); }
      else setError('Reve introuvable');
      setIsLoading(false);
      return;
    }
    try {
      const data = await dreamsApi.getViewer(dreamId);
      setViewerData(data);
      setCurrentDream(dreamId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setIsLoading(false);
    }
  };

  const imageCount = viewerData?.images.length ?? 0;
  const isVertical = navigationMode === 'scroll';

  // Triple images for seamless infinite loop
  const allImages = viewerData
    ? [...viewerData.images, ...viewerData.images, ...viewerData.images]
    : [];

  // Position scroll at middle set on mount / mode change
  useLayoutEffect(() => {
    if (!scrollRef.current || imageCount === 0) return;
    const el = scrollRef.current;
    const size = isVertical ? window.innerHeight : window.innerWidth;
    const target = size * (imageCount + currentIndex);
    if (isVertical) {
      el.scrollTop = target;
    } else {
      el.scrollLeft = target;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerData, isVertical, imageCount, navigationMode]);

  // Track current image + loop boundaries
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || isResetting.current || imageCount === 0) return;

    const size = isVertical ? window.innerHeight : window.innerWidth;
    const pos = isVertical ? el.scrollTop : el.scrollLeft;

    // Current image (within original set)
    const rawIdx = Math.round(pos / size);
    const idx = ((rawIdx % imageCount) + imageCount) % imageCount;
    setCurrentIndex(idx);

    // Loop: jumped into first or last set → teleport to middle set
    const middleStart = size * imageCount;
    const middleEnd = size * imageCount * 2;

    if (pos < middleStart) {
      isResetting.current = true;
      if (isVertical) el.scrollTop = pos + size * imageCount;
      else el.scrollLeft = pos + size * imageCount;
      requestAnimationFrame(() => { isResetting.current = false; });
    } else if (pos >= middleEnd) {
      isResetting.current = true;
      if (isVertical) el.scrollTop = pos - size * imageCount;
      else el.scrollLeft = pos - size * imageCount;
      requestAnimationFrame(() => { isResetting.current = false; });
    }
  }, [imageCount, isVertical]);

  // Tap detection (distinguish from scroll/drag gesture)
  const handlePointerDown = (e: React.PointerEvent) => {
    pointerStart.current = { x: e.clientX, y: e.clientY, t: Date.now() };
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    const dx = Math.abs(e.clientX - pointerStart.current.x);
    const dy = Math.abs(e.clientY - pointerStart.current.y);
    const dt = Date.now() - pointerStart.current.t;
    if (dx < 10 && dy < 10 && dt < 300 && !showWallpaperSheet && !showSettingsSheet) {
      toggleBottomBar();
    }
  };

  // Favorite toggle
  const handleToggleFavorite = async () => {
    if (!viewerData) return;
    const img = viewerData.images[currentIndex];
    if (!img) return;
    if (isMock) {
      setViewerData((prev) => prev ? ({
        ...prev,
        images: prev.images.map((im, i) =>
          i === currentIndex ? { ...im, isFavorite: !im.isFavorite } : im
        ),
      }) : null);
      return;
    }
    try {
      await assetsApi.update(img.id, { isFavorite: !img.isFavorite });
      setViewerData((prev) => prev ? ({
        ...prev,
        images: prev.images.map((im, i) =>
          i === currentIndex ? { ...im, isFavorite: !im.isFavorite } : im
        ),
      }) : null);
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  const anyOverlayOpen = showWallpaperSheet || showSettingsSheet || isBottomBarVisible;

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-system-carbon-500">
        <div className="w-12 h-12 border-4 rounded-full animate-spin"
          style={{ borderColor: 'rgba(255,255,255,0.1)', borderTopColor: 'rgba(255,255,255,0.5)' }}
        />
      </div>
    );
  }

  // Error / empty
  if (error || !viewerData || viewerData.images.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-system-carbon-500 px-6">
        <p className="text-ivory-muted text-center mb-4">{error || 'Aucune image disponible'}</p>
        <button onClick={() => navigate('/dreams')} className="text-white/60 hover:text-white transition-colors">
          Retour aux reves
        </button>
      </div>
    );
  }

  const currentImage = viewerData.images[currentIndex];

  return (
    <div className="viewer-fullscreen bg-system-carbon-500">
      {/* Continuous image strip — native scroll, no page transitions */}
      <div
        ref={scrollRef}
        className={`w-full h-full scrollbar-hide ${
          isVertical
            ? 'overflow-y-auto overflow-x-hidden'
            : 'overflow-x-auto overflow-y-hidden flex'
        }`}
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          ...(anyOverlayOpen ? { overflow: 'hidden' } : {}),
        }}
        onScroll={handleScroll}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
      >
        {allImages.map((img, i) => {
          const inMiddle = i >= imageCount && i < imageCount * 2;
          return (
            <div
              key={i}
              className="flex-shrink-0"
              style={{
                width: isVertical ? '100%' : '100vw',
                height: isVertical ? '100vh' : '100%',
              }}
            >
              <img
                src={img.url}
                alt=""
                className="w-full h-full object-cover select-none"
                draggable={false}
                loading={inMiddle ? 'eager' : 'lazy'}
              />
            </div>
          );
        })}
      </div>

      {/* Bottom scrim */}
      <div className="absolute bottom-0 left-0 right-0 h-32 scrim-bottom z-10 pointer-events-none" />

      {/* Bottom handle */}
      <div className="absolute bottom-0 left-0 right-0 h-16 flex items-end justify-center pb-4 z-20 pointer-events-none">
        <motion.div
          animate={{ y: isBottomBarVisible ? 10 : 0 }}
          className="w-10 h-1 bg-white/30 rounded-full"
        />
      </div>

      {/* Bottom action bar — icons only */}
      <AnimatePresence>
        {isBottomBarVisible && (
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="bottom-action-bar z-30"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-center mb-5">
                <button onClick={() => hideBottomBar()} className="w-10 h-1 bg-white/15 rounded-full hover:bg-white/30 transition-colors" />
              </div>

              <div className="flex justify-around">
                <button
                  onClick={handleToggleFavorite}
                  className={`p-3 rounded-xl transition-colors ${
                    currentImage?.isFavorite ? 'text-red-500' : 'text-white/40 hover:text-white/70'
                  }`}
                >
                  <Heart className={`w-6 h-6 ${currentImage?.isFavorite ? 'fill-current' : ''}`} />
                </button>
                <button
                  onClick={() => { setShowWallpaperSheet(true); hideBottomBar(); }}
                  className="p-3 rounded-xl text-white/40 hover:text-white/70 transition-colors"
                >
                  <Download className="w-6 h-6" />
                </button>
                <button
                  onClick={() => { setShowSettingsSheet(true); hideBottomBar(); }}
                  className="p-3 rounded-xl text-white/40 hover:text-white/70 transition-colors"
                >
                  <Settings className="w-6 h-6" />
                </button>
              </div>

              <p className="text-center text-ivory-faint text-xs mt-5">
                {currentIndex + 1} / {imageCount}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Wallpaper sheet */}
      <AnimatePresence>
        {showWallpaperSheet && currentImage && (
          <WallpaperSheet
            imageUrl={currentImage.url}
            imageId={currentImage.id}
            isMock={isMock}
            onClose={() => setShowWallpaperSheet(false)}
          />
        )}
      </AnimatePresence>

      {/* Settings sheet — scroll/swipe + vitesse only */}
      <AnimatePresence>
        {showSettingsSheet && (
          <ViewerSettingsSheet onClose={() => setShowSettingsSheet(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ==========================================
// Viewer Settings — scroll/swipe + vitesse
// ==========================================

function ViewerSettingsSheet({ onClose }: { onClose: () => void }) {
  const {
    navigationMode,
    gestureSensitivity,
    setNavigationMode,
    setGestureSensitivity,
  } = useSettingsStore();

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="glass-card-elevated rounded-t-3xl rounded-b-none">
          <div className="p-6 space-y-5">
            {/* Handle */}
            <div className="flex justify-center">
              <div className="w-10 h-1 bg-white/15 rounded-full" />
            </div>

            {/* Scroll / Swipe — icons only */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { mode: 'scroll' as const, icon: Scroll },
                { mode: 'swipe' as const, icon: Hand },
              ].map((item) => (
                <button
                  key={item.mode}
                  onClick={() => setNavigationMode(item.mode)}
                  className={`p-4 rounded-xl flex items-center justify-center transition-all duration-200 ${
                    navigationMode === item.mode
                      ? 'border border-white/25'
                      : 'border border-transparent'
                  }`}
                  style={{
                    background: navigationMode === item.mode
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgba(255,255,255,0.03)',
                  }}
                >
                  <item.icon className={`w-6 h-6 ${navigationMode === item.mode ? 'text-white/90' : 'text-white/35'}`} />
                </button>
              ))}
            </div>

            {/* Vitesse */}
            <input
              type="range" min="0" max="1" step="0.1"
              value={gestureSensitivity}
              onChange={(e) => setGestureSensitivity(parseFloat(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.1)' }}
            />

            <div className="h-2" />
          </div>
        </div>
      </motion.div>
    </>
  );
}

// ==========================================
// Wallpaper sheet
// ==========================================

function WallpaperSheet({
  imageUrl,
  imageId,
  isMock,
  onClose,
}: {
  imageUrl: string;
  imageId: string;
  isMock: boolean;
  onClose: () => void;
}) {
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [emailError, setEmailError] = useState('');

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setEmailError('Adresse email invalide');
      return;
    }

    setEmailError('');
    setIsSending(true);

    if (isMock) {
      await new Promise((r) => setTimeout(r, 1200));
      setIsSent(true);
      setIsSending(false);
      return;
    }

    try {
      await assetsApi.getWallpaper(imageId);
      setIsSent(true);
    } catch {
      setEmailError('Erreur lors de l\'envoi. Reessayez.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-50 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="glass-card-elevated rounded-t-3xl rounded-b-none">
          <div className="p-6 space-y-5">
            <div className="flex justify-center">
              <div className="w-10 h-1 bg-white/15 rounded-full" />
            </div>

            <div className="flex items-center gap-3">
              <button onClick={onClose} className="text-white/40 hover:text-white/70 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h2 className="text-lg font-display font-bold">Fond d'ecran</h2>
            </div>

            <div className="flex justify-center">
              <div className="w-32 h-56 rounded-2xl overflow-hidden shadow-lg" style={{ border: '2px solid rgba(255,255,255,0.1)' }}>
                <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
              </div>
            </div>

            <div className="glass-card p-4 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Smartphone className="w-4 h-4 text-white/40" />
                <h3 className="text-sm font-semibold text-white/80">Comment installer</h3>
              </div>
              <div className="space-y-2">
                {[
                  'Enregistrez l\'image dans vos photos (maintenez appuye puis "Enregistrer")',
                  'Ouvrez Reglages > Fond d\'ecran',
                  'Choisissez l\'image et selectionnez Ecran verrouille, Ecran d\'accueil ou les deux',
                ].map((text, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}>{i + 1}</span>
                    <p className="text-sm text-white/50">{text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-white/40" />
                <h3 className="text-sm font-semibold text-white/80">Recevoir par email</h3>
              </div>
              <p className="text-xs text-ivory-faint">
                Recevez l'image adaptee a votre telephone directement dans votre boite mail.
              </p>

              {isSent ? (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 rounded-xl"
                  style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)' }}
                >
                  <Check className="w-4 h-4 text-emerald-400" />
                  <p className="text-sm text-emerald-400/80">Email envoye !</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSendEmail} className="space-y-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                    placeholder="votre@email.com"
                    className="input-glass text-sm"
                    disabled={isSending}
                  />
                  {emailError && <p className="text-red-400 text-xs">{emailError}</p>}
                  <button
                    type="submit"
                    disabled={!email.trim() || isSending}
                    className="btn-primary w-full py-3 flex items-center justify-center gap-2 text-sm"
                  >
                    {isSending ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /><span>Envoi...</span></>
                    ) : (
                      <><Mail className="w-4 h-4" /><span>Envoyer</span></>
                    )}
                  </button>
                </form>
              )}
            </div>

            <div className="h-4" />
          </div>
        </div>
      </motion.div>
    </>
  );
}
